import React from 'react';
import {
    Box,
    Stack,
    Typography,
    FormControl,
    Select,
    MenuItem,
    Chip,
    SelectChangeEvent,
} from '@mui/material';
import { TrendingUp as TrendingUpIcon } from '@mui/icons-material';

interface ChartPoint {
    label: string;
    value: number;
}

interface RatingChartProps {
    points: ChartPoint[];
    daysRange: number;
    setDaysRange: (n: number) => void;
    usingDummy: boolean;
}

const CHART_HEIGHT = 160;

export default function RatingChart({ points, daysRange, setDaysRange, usingDummy }: RatingChartProps) {
    const w = 900;
    const h = CHART_HEIGHT;
    const pad = 36;
    const max = 5;
    const stepX = (w - pad * 2) / Math.max(1, points.length - 1);

    const path = points.map((p, i) => {
        const x = pad + i * stepX;
        const y = pad + (1 - (p.value / max)) * (h - pad * 2);
        return `${i === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`;
    }).join(' ');

    return (
        <Box sx={{ overflowX: 'auto' }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                    <Typography variant="subtitle1" fontWeight={700}>
                        <TrendingUpIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'primary.main' }} />
                        Ratings Trend
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        last {daysRange} days
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: 80 }}>
                        <Select
                            value={daysRange}
                            onChange={(e: SelectChangeEvent<number>) => setDaysRange(Number(e.target.value))}
                            sx={{ borderRadius: 2 }}
                        >
                            <MenuItem value={7}>7d</MenuItem>
                            <MenuItem value={14}>14d</MenuItem>
                            <MenuItem value={30}>30d</MenuItem>
                        </Select>
                    </FormControl>
                    {usingDummy && (
                        <Chip label="Preview data" size="small" variant="outlined" color="info" />
                    )}
                </Stack>
            </Stack>
            <svg width={Math.min(w, 900)} height={h} viewBox={`0 0 ${w} ${h}`} style={{ width: '100%', marginTop: 8 }}>
                {[0, 1, 2, 3, 4, 5].map((g) => {
                    const y = pad + (1 - g / max) * (h - pad * 2);
                    return <line key={g} x1={pad} x2={w - pad} y1={y} y2={y} stroke="#eef3fb" strokeWidth={1} />;
                })}
                <path d={`${path} L ${w - pad} ${h - pad} L ${pad} ${h - pad} Z`} fill="rgba(102, 126, 234, 0.1)" stroke="none" />
                <path d={path} fill="none" stroke="#667eea" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" />
                {points.map((p, i) => {
                    const x = pad + i * stepX;
                    const y = pad + (1 - (p.value / max)) * (h - pad * 2);
                    return <circle key={i} cx={x} cy={y} r={4} fill={p.value ? '#667eea' : '#e6edf3'} stroke="#fff" strokeWidth={2} />;
                })}
                {(() => {
                    const labelStep = Math.max(1, Math.ceil(points.length / 8));
                    return points.map((p, i) => {
                        const x = pad + i * stepX;
                        if (i % labelStep !== 0 && i !== points.length - 1) return null;
                        return <text key={i} x={x} y={h - 10} fontSize={11} textAnchor="middle" fill="#64748b">{p.label}</text>;
                    });
                })()}
                {[0, 1, 2, 3, 4, 5].map((v, i) => {
                    const y = pad + (1 - (v / max)) * (h - pad * 2);
                    return <text key={i} x={6} y={y + 4} fontSize={10} fill="#94a3b8">{v}</text>;
                })}
            </svg>
        </Box>
    );
}
